import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Check, X, GraduationCap, CheckCircle2, Circle, XCircle, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { apiService } from '@/lib/api-functions/api-service';
import { TutorialTask, UserTutorialProgress } from '@/lib/stores/storeTypes';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';
import { useToast } from '@/hooks/use-toast';
import { getUserRoleInCompany } from '@/lib/stores/unifiedUserStore';

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
interface TaskWithProgress extends TutorialTask {
  progress?: UserTutorialProgress;
  isApplicableToRole: boolean;
}

const TutorialPage: React.FC = () => {
  const [tasks, setTasks] = useState<TaskWithProgress[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { selectedCompany, currentUser } = useSimplifiedUserData();
  const { toast } = useToast();
  
  // Fetch tutorial tasks
  const fetchTasks = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await apiService.getTutorialTasks();
      
      if (result.success && result.data) {
        // Get user's role in selected company
        const selectedCompanyId = selectedCompany?.id;
        const userId = currentUser?.id;
        const userRole = selectedCompanyId && userId
          ? getUserRoleInCompany(selectedCompanyId, userId)
          : null;

        // Process tasks to determine role applicability
        const processedTasks = result.data.map((task: any) => ({
          ...task,
          isApplicableToRole: !task.role_applicability?.length || 
            task.role_applicability.includes(userRole || '') ||
            task.role_applicability.includes(userRole?.toLowerCase() || '')
        }));

        setTasks(processedTasks);
      }
    } catch (error) {
      console.error('Failed to fetch tutorial tasks:', error);
      toast({
        title: 'Error',
        description: 'Failed to load tutorial tasks',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [selectedCompany, currentUser, toast]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  // Calculate progress
  const progress = useMemo(() => {
    const completed = tasks.filter(t => t.progress?.completed_at).length;
    const total = tasks.length;
    const percentage = total > 0 ? (completed / total) * 100 : 0;
    
    return {
      completed,
      total,
      percentage
    };
  }, [tasks]);

  // Mark task as complete
  const handleMarkComplete = async (taskId: string) => {
    try {
      const result = await apiService.markTaskComplete(taskId);
      
      if (result.success) {
        toast({
          title: 'Success',
          description: 'Task marked as complete!',
        });
        fetchTasks(); // Refresh tasks
      } else {
        toast({
          title: 'Error',
          description: 'Failed to mark task as complete',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Failed to mark task complete:', error);
      toast({
        title: 'Error',
        description: 'Failed to mark task as complete',
        variant: 'destructive',
      });
    }
  };

  // Skip task
  const handleSkipTask = async (taskId: string) => {
    try {
      const result = await apiService.skipTask(taskId);
      
      if (result.success) {
        toast({
          title: 'Success',
          description: 'Task skipped',
        });
        fetchTasks(); // Refresh tasks
      } else {
        toast({
          title: 'Error',
          description: 'Failed to skip task',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Failed to skip task:', error);
      toast({
        title: 'Error',
        description: 'Failed to skip task',
        variant: 'destructive',
      });
    }
  };

  // Get status info for a task
  const getTaskStatus = (task: TaskWithProgress) => {
  const { t } = usePlatformTranslation();
    if (task.progress?.completed_at) {
      return {
        label: 'Completed',
        icon: <CheckCircle2 className="h-5 w-5 text-green-500" />,
        color: 'text-green-600'
      };
    }
    if (task.progress?.skipped) {
      return {
        label: 'Skipped',
        icon: <XCircle className="h-5 w-5 text-gray-400" />,
        color: 'text-gray-500'
      };
    }
    if (!task.isApplicableToRole) {
      return {
        label: 'Not for your role',
        icon: <Circle className="h-5 w-5 text-gray-300" />,
        color: 'text-gray-400'
      };
    }
    return {
      label: 'Not Started',
      icon: <Circle className="h-5 w-5 text-gray-300" />,
      color: 'text-gray-600'
    };
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">{t('platform.pages.tutorialpage.loading_tutorial')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-4xl mx-auto p-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <GraduationCap className="h-8 w-8 text-primary" />
            <h1 className="text-3xl font-bold text-center">{t('platform.pages.tutorialpage.platform_tutorial')}</h1>
          </div>
          <p className="text-center text-muted-foreground mb-6">
            Complete these interactive tasks to learn how to use the platform effectively
          </p>

          {/* Progress Bar */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-center">{t('platform.pages.tutorialpage.your_progress')}</CardTitle>
                <Badge variant="secondary" className="text-lg px-4">
                  {progress.completed} / {progress.total}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <Progress value={progress.percentage} className="h-3" />
              <p className="text-center text-sm text-muted-foreground mt-2">
                {progress.percentage.toFixed(0)}% Complete
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Task List */}
        <div className="space-y-4">
          {tasks.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Sparkles className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">
                  No tutorial tasks available at the moment.
                </p>
              </CardContent>
            </Card>
          ) : (
            tasks.map((task, index) => {
              const status = getTaskStatus(task);
              const isCompleted = !!task.progress?.completed_at;
              const isSkipped = task.progress?.skipped;
              const canInteract = task.isApplicableToRole && !isCompleted && !isSkipped;

              return (
                <Card
                  key={task.id}
                  className={cn(
                    'transition-all',
                    isCompleted && 'bg-green-50/50 border-green-200 dark:bg-green-950/20',
                    !task.isApplicableToRole && 'opacity-60'
                  )}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          {status.icon}
                          <CardTitle className="text-xl">
                            {index + 1}. {task.title}
                          </CardTitle>
                        </div>
                        <CardDescription className="text-base">
                          {task.description}
                        </CardDescription>
                      </div>
                      <Badge
                        variant={isCompleted ? 'default' : 'outline'}
                        className={cn('text-center whitespace-nowrap', status.color)}
                      >
                        {status.label}
                      </Badge>
                    </div>
                  </CardHeader>

                  {/* Role Applicability & Actions */}
                  <CardContent>
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        {!task.isApplicableToRole && (
                          <Badge variant="secondary" className="text-xs">
                            Not applicable to your role
                          </Badge>
                        )}
                        {task.role_applicability && task.role_applicability.length > 0 && (
                          <span className="text-xs">
                            For: {task.role_applicability.join(', ')}
                          </span>
                        )}
                        {(!task.role_applicability || task.role_applicability.length === 0) && (
                          <span className="text-xs">{t('platform.pages.tutorialpage.for_all_roles')}</span>
                        )}
                      </div>

                      {canInteract && (
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleSkipTask(task.id)}
                          >
                            <X className="h-4 w-4 mr-1" />
                            Skip
                          </Button>
                          <Button
                            variant="default"
                            size="sm"
                            onClick={() => handleMarkComplete(task.id)}
                          >
                            <Check className="h-4 w-4 mr-1" />
                            Mark Complete
                          </Button>
                        </div>
                      )}
                    </div>

                    {!isCompleted && !isSkipped && task.isApplicableToRole && (
                      <div className="mt-3 p-3 bg-primary/5 rounded-md border border-primary/20">
                        <p className="text-sm text-center text-muted-foreground">
                          <Sparkles className="inline h-4 w-4 mr-1" />
                          Complete this action in the platform to automatically check it off,
                          or mark it manually when done.
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>

        {/* Footer Note */}
        <div className="mt-8 text-center text-sm text-muted-foreground">
          <p>{t('platform.pages.tutorialpage.you_can_return_to_this_tutorial_anytime_from_the_sidebar_menu')}</p>
          <p className="mt-1">{t('platform.pages.tutorialpage.tasks_will_automatically_be_marked_complete_as_you_use_the_platform')}</p>
        </div>
      </div>
    </div>
  );
};

export default TutorialPage;

